import { Box, Stack, Tab, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { PageLoader } from '@/components/shared/PageLoader';
import { FilterAndSearchPanel } from '@/components/shared/FilterAndSearchPanel';
import { StructureCheck } from '@/components/shared/StructureCheck';
import { ReportInternalType, ReportStatus, useAllReportsListQuery } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { FloatingLine, Panel, ReportTabs, ReportsMainStyles, containerStyles } from './styles';
import { Snackbar } from '@/components/shared/Snackbar';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { AccessGuard } from '@/components/Analysis/shared/AccessGuard';
import { ReportListingAccordian } from './ReportListingAccordian';
import { UpgradePromptBackground } from '@/types';
import { UpgradePrompt } from '../../shared/UpgradePrompt';

export const ReportsLanding = () => {
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);
  const [currentTab, setCurrentTab] = useState<ReportStatus>(ReportStatus.Draft);
  const [searchTerm, setSearchTerm] = useState<string | undefined>();
  const structureId = useGetStructureId();
  const { shouldShow, content } = snackbarMessage;
  const navbarHeight = useNavbarHeight();

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };

  const reportsTabs = [
    { key: ReportStatus.Draft, label: 'Drafts' },
    { key: ReportStatus.Published, label: 'Open' },
    { key: ReportStatus.Closed, label: 'Closed' },
  ];

  const {
    data: allReportsData,
    loading,
    error,
  } = useAllReportsListQuery({
    variables: {
      input: {
        structureId,
        status: currentTab,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const filteredReports = useMemo(() => {
    if (!allReportsData?.allReports) return [];

    if (searchTerm)
      return allReportsData?.allReports.filter((report) =>
        report?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

    return allReportsData?.allReports;
  }, [allReportsData?.allReports, searchTerm]);

  const handleTabActiveChange = (event: React.SyntheticEvent, newValue: ReportStatus) => {
    setCurrentTab(newValue);
  };

  // Filtering for null internalType, because of legacy data
  // old records wont have internalType
  const poiReports = useMemo(() => {
    return filteredReports.filter(
      (report) =>
        !report?.internalType || report?.internalType === ReportInternalType.PointOfInterest
    );
  }, [filteredReports]);

  const paintBlockReports = useMemo(() => {
    return filteredReports.filter(
      (report) => report?.internalType === ReportInternalType.PaintBlock
    );
  }, [filteredReports]);

  const componentReports = useMemo(() => {
    return filteredReports.filter(
      (report) => report?.internalType === ReportInternalType.Component
    );
  }, [filteredReports]);

  if (!structureId || structureId === '') {
    return <div>Please specify inspection id</div>;
  }

  if (loading) {
    return (
      <Stack direction="row" justifyContent="center" marginTop={8}>
        <PageLoader />
      </Stack>
    );
  }

  if (
    // Assume its an error if data is null even after loading is done
    !allReportsData ||
    // If its actually an error
    error
  ) {
    return (
      <Stack justifyContent="center" marginTop={'20%'} textAlign={'center'}>
        <Typography my={2} variant="h6">
          An unknown error has occurred please contact support
        </Typography>

        <Typography>Error code: {error?.message || 'N/A'}</Typography>
      </Stack>
    );
  }

  return (
    <StructureCheck structureId={structureId}>
      <AccessGuard structureId={structureId} feature="reports">
        <UpgradePrompt
          feature="reports"
          featureTitle="Reports"
          background={UpgradePromptBackground.Reports}
          paragraphs={[
            'Access enhanced reports with automated site images',
            'View marked location of anomalies',
            'Export reports for streamlined tracking, seamless monitoring, and documentation purposes',
            'Add selected images of anomalies for greater clarity',
          ]}
        >
          <Stack
            direction="row"
            justifyContent="center"
            sx={containerStyles}
            height={`calc(100vh - ${navbarHeight}px)`}
          >
            <Box sx={ReportsMainStyles}>
              <ReportTabs value={currentTab} onChange={handleTabActiveChange}>
                {reportsTabs.map((tab) => (
                  <Tab
                    value={tab.key}
                    key={tab.key}
                    label={tab.label}
                    sx={{ textTransform: 'none' }}
                  />
                ))}
              </ReportTabs>
              <FloatingLine />
              <Panel>
                <FilterAndSearchPanel
                  templateFields={[]}
                  selectedValues={{}}
                  setSelectedValues={() => {}}
                  handleSelectedChange={() => {}}
                  searchBar
                  searchPlaceholder="Search report"
                  searchTerm={searchTerm}
                  setSearch={setSearchTerm}
                  filterOptions={false}
                />
              </Panel>
              <Box>
                <ReportListingAccordian reports={paintBlockReports} label={'Paint Block'} />
                <ReportListingAccordian reports={poiReports} label={'Point of Interest'} />
                <ReportListingAccordian reports={componentReports} label={'Component'} />
              </Box>
              <Snackbar open={shouldShow} onClose={closeSnack}>
                {content}
              </Snackbar>
            </Box>
          </Stack>
        </UpgradePrompt>
      </AccessGuard>
    </StructureCheck>
  );
};
